import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Plus, FileText, Calendar as CalendarIcon, Download, Send, CheckCircle, AlertCircle, Trash2, CreditCard } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const InvoicesPage = () => {
  const { api, user, token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    tournament_event: '',
    location: '',
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    tax_rate: 0,
    line_items: []
  });

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes, gamesRes] = await Promise.all([
        api().get('/invoices'),
        api().get('/clients'),
        api().get('/games')
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setGames(gamesRes.data.filter(g => g.status === 'completed'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        { game_date: '', division: '', num_games: 1, officials_needed: 2, rate: 50.0, line_total: 100.0 }
      ]
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    // Recalculate line total
    if (field === 'num_games' || field === 'officials_needed' || field === 'rate') {
      items[index].line_total = items[index].num_games * items[index].officials_needed * items[index].rate;
    }
    setFormData({ ...formData, line_items: items });
  };

  const removeLineItem = (index) => {
    const items = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: items });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.line_items.length === 0) {
      toast.error('Add at least one line item');
      return;
    }
    try {
      await api().post('/invoices', formData);
      toast.success('Invoice created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleStatusChange = async (invoiceId, status) => {
    try {
      await api().put(`/invoices/${invoiceId}/status?status=${status}`);
      toast.success(`Invoice marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api().delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const downloadPDF = (invoiceId) => {
    window.open(`${BACKEND_URL}/api/invoices/${invoiceId}/pdf`, '_blank');
  };

  const initiatePayment = async (invoiceId) => {
    try {
      const response = await api().post('/payments/checkout', {
        invoice_id: invoiceId,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      tournament_event: '',
      location: '',
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      tax_rate: 0,
      line_items: []
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-zinc-800 text-zinc-300 border-zinc-700',
      sent: 'bg-blue-900/30 text-blue-400 border-blue-900',
      paid: 'bg-green-900/30 text-green-400 border-green-900',
      overdue: 'bg-red-900/30 text-red-400 border-red-900'
    };
    return colors[status] || colors.draft;
  };

  const canEdit = user?.role === 'admin' || user?.role === 'assignor';
  const isClient = user?.role === 'client';

  const calculateSubtotal = () => {
    return formData.line_items.reduce((sum, item) => sum + item.line_total, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="invoices-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Invoices</h1>
          <p className="text-muted-foreground mt-1">
            {isClient ? 'View and pay your invoices' : 'Create and manage invoices'}
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-invoice-btn" className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm">
                <Plus className="w-4 h-4 mr-2" />
                Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">New Invoice</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Client</Label>
                    <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                      <SelectTrigger data-testid="invoice-client-select" className="bg-zinc-800 border-zinc-700 h-10 rounded-sm">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>{client.organization_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Due Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="invoice-due-date-picker" className="w-full justify-start text-left bg-zinc-800 border-zinc-700 h-10 rounded-sm">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.due_date, 'PPP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                        <Calendar
                          mode="single"
                          selected={formData.due_date}
                          onSelect={(date) => date && setFormData({ ...formData, due_date: date })}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Tournament/Event</Label>
                    <Input
                      value={formData.tournament_event}
                      onChange={(e) => setFormData({ ...formData, tournament_event: e.target.value })}
                      placeholder="State Championship"
                      required
                      data-testid="invoice-event-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Denver, CO"
                      required
                      data-testid="invoice-location-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Tax Rate (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                    data-testid="invoice-tax-input"
                    className="bg-zinc-800 border-zinc-700 h-10 rounded-sm w-32"
                  />
                </div>

                {/* Line Items */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Line Items</Label>
                    <Button type="button" onClick={addLineItem} variant="outline" size="sm" data-testid="add-line-item-btn" className="border-zinc-700">
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>

                  {formData.line_items.length === 0 ? (
                    <div className="text-center py-4 text-zinc-500 bg-zinc-800/50 border border-dashed border-zinc-700">
                      Click "Add Item" to add line items
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {formData.line_items.map((item, index) => (
                        <div key={index} className="grid grid-cols-6 gap-2 items-end p-3 bg-zinc-800/50 border border-zinc-700">
                          <div>
                            <Label className="text-xs text-zinc-500">Date</Label>
                            <Input
                              value={item.game_date}
                              onChange={(e) => updateLineItem(index, 'game_date', e.target.value)}
                              placeholder="01/15/2024"
                              className="bg-zinc-800 border-zinc-700 h-8 text-sm rounded-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500">Division</Label>
                            <Input
                              value={item.division}
                              onChange={(e) => updateLineItem(index, 'division', e.target.value)}
                              placeholder="Varsity"
                              className="bg-zinc-800 border-zinc-700 h-8 text-sm rounded-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500"># Games</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.num_games}
                              onChange={(e) => updateLineItem(index, 'num_games', parseInt(e.target.value) || 1)}
                              className="bg-zinc-800 border-zinc-700 h-8 text-sm rounded-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500">Officials</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.officials_needed}
                              onChange={(e) => updateLineItem(index, 'officials_needed', parseInt(e.target.value) || 1)}
                              className="bg-zinc-800 border-zinc-700 h-8 text-sm rounded-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500">Rate</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                              className="bg-zinc-800 border-zinc-700 h-8 text-sm rounded-sm"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-emerald-400 font-medium">{formatCurrency(item.line_total)}</span>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)} className="text-red-400 hover:text-red-300 h-8 w-8 p-0">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Summary */}
                {formData.line_items.length > 0 && (
                  <div className="border-t border-zinc-700 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">Subtotal:</span>
                      <span>{formatCurrency(calculateSubtotal())}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">Tax ({formData.tax_rate}%):</span>
                      <span>{formatCurrency(calculateSubtotal() * (formData.tax_rate / 100))}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span className="text-emerald-400">{formatCurrency(calculateSubtotal() * (1 + formData.tax_rate / 100))}</span>
                    </div>
                  </div>
                )}

                <Button type="submit" data-testid="invoice-submit-btn" className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm h-10">
                  Create Invoice
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800">
          <FileText className="w-12 h-12 text-zinc-600 mb-4" />
          <p className="text-zinc-500 text-lg">No invoices yet</p>
          {canEdit && <p className="text-zinc-600 text-sm mt-2">Click "Create Invoice" to create your first invoice</p>}
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <div 
              key={invoice.id}
              data-testid={`invoice-card-${invoice.id}`}
              className="bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
            >
              <div className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-emerald-900/20 rounded-sm">
                      <FileText className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-mono text-lg">{invoice.invoice_number}</h3>
                      <p className="text-zinc-400">{invoice.client_info?.organization_name || 'Unknown Client'}</p>
                      <p className="text-sm text-zinc-500">{invoice.tournament_event} • {invoice.location}</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-heading font-bold text-emerald-400">{formatCurrency(invoice.total_due)}</p>
                      <p className="text-sm text-zinc-500">Due: {formatDate(invoice.due_date)}</p>
                    </div>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full border self-start sm:self-center ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-zinc-800 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadPDF(invoice.id)}
                    data-testid={`download-pdf-btn-${invoice.id}`}
                    className="border-zinc-700"
                  >
                    <Download className="w-4 h-4 mr-1" /> Download PDF
                  </Button>

                  {invoice.status !== 'paid' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => initiatePayment(invoice.id)}
                      data-testid={`pay-invoice-btn-${invoice.id}`}
                      className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/20"
                    >
                      <CreditCard className="w-4 h-4 mr-1" /> Pay Now
                    </Button>
                  )}

                  {canEdit && (
                    <>
                      {invoice.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusChange(invoice.id, 'sent')}
                          data-testid={`send-invoice-btn-${invoice.id}`}
                          className="border-blue-700 text-blue-400 hover:bg-blue-900/20"
                        >
                          <Send className="w-4 h-4 mr-1" /> Mark Sent
                        </Button>
                      )}
                      {invoice.status !== 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusChange(invoice.id, 'paid')}
                          data-testid={`mark-paid-btn-${invoice.id}`}
                          className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/20"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Mark Paid
                        </Button>
                      )}
                      {invoice.status === 'sent' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusChange(invoice.id, 'overdue')}
                          data-testid={`mark-overdue-btn-${invoice.id}`}
                          className="border-red-700 text-red-400 hover:bg-red-900/20"
                        >
                          <AlertCircle className="w-4 h-4 mr-1" /> Mark Overdue
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(invoice.id)}
                        data-testid={`delete-invoice-btn-${invoice.id}`}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
